import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Send, AlertCircle } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { INDUSTRY_OPTIONS, COUNTRIES } from "@/lib/data";

export default function LeadForm({ source = "contact", compact = false }) {
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    country: "", industry: "", power_requirement: "",
    project_location: "", timeline: "", budget: "", message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");
    try {
      await api.post("/leads", { ...form, source });
      setStatus("success");
      setForm({ name: "", company: "", email: "", phone: "", country: "", industry: "", power_requirement: "", project_location: "", timeline: "", budget: "", message: "" });
    } catch (e) {
      setStatus("error");
      setErrorMsg(formatError(e));
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="panel p-10 text-center"
        data-testid="lead-form-success"
      >
        <div className="w-12 h-12 mx-auto border border-tratou-green/60 flex items-center justify-center mb-5">
          <Check className="w-6 h-6 text-tratou-green" />
        </div>
        <h3 className="font-display text-2xl font-bold mb-2">Transmission received.</h3>
        <p className="text-tratou-dim max-w-md mx-auto">
          A Tratou energy strategist will reach out within 1 business day with a tailored proposal.
        </p>
        <button onClick={() => setStatus("idle")} className="btn-secondary mt-6" data-testid="lead-form-reset">
          Send another request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} className="panel p-6 md:p-10" data-testid="lead-form">
      <div className={`grid gap-5 ${compact ? "md:grid-cols-2" : "md:grid-cols-2"}`}>
        <Field label="Full Name *" name="name" value={form.name} onChange={update("name")} required testid="lf-name" />
        <Field label="Company" name="company" value={form.company} onChange={update("company")} testid="lf-company" />
        <Field label="Work Email *" name="email" type="email" value={form.email} onChange={update("email")} required testid="lf-email" />
        <Field label="Phone" name="phone" value={form.phone} onChange={update("phone")} testid="lf-phone" />

        <Select label="Country" value={form.country} onChange={update("country")} options={COUNTRIES} testid="lf-country" />
        <Select label="Industry" value={form.industry} onChange={update("industry")} options={INDUSTRY_OPTIONS} testid="lf-industry" />

        <Field label="Power Requirement (kW / kWh)" value={form.power_requirement} onChange={update("power_requirement")} placeholder="e.g. 200 kW peak, 600 kWh/day" testid="lf-power" />
        <Field label="Project Location" value={form.project_location} onChange={update("project_location")} placeholder="City, region" testid="lf-location" />

        <Select label="Timeline" value={form.timeline} onChange={update("timeline")} options={["Immediate (<30 days)", "1–3 months", "3–6 months", "6–12 months", "Exploratory"]} testid="lf-timeline" />
        <Select label="Budget" value={form.budget} onChange={update("budget")} options={["< $50K", "$50K – $250K", "$250K – $1M", "$1M – $5M", "$5M+"]} testid="lf-budget" />

        <div className="md:col-span-2">
          <label className="field-label">Message</label>
          <textarea
            value={form.message}
            onChange={update("message")}
            rows={4}
            placeholder="Tell us about your site, load profile, and timeline..."
            className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
            data-testid="lf-message"
          />
        </div>
      </div>

      {errorMsg && (
        <div className="mt-5 flex items-start gap-2 text-red-400 text-sm" data-testid="lead-form-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          disabled={status === "loading"}
          className="btn-primary disabled:opacity-50"
          data-testid="lead-form-submit"
        >
          {status === "loading" ? "Transmitting..." : (source === "demo" ? "Schedule Demo" : "Request Quote")}
          <Send className="w-4 h-4" />
        </button>
        <p className="text-xs text-tratou-faint font-mono uppercase tracking-[0.18em] sm:self-center">
          Reply within 1 business day · No spam
        </p>
      </div>
    </form>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm"
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      <select
        value={value}
        onChange={onChange}
        className="w-full bg-tratou-void/80 border border-white/10 px-4 py-3 outline-none focus:border-tratou-green/60 text-white text-sm font-sans"
        data-testid={testid}
      >
        <option value="">Select...</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
